/**
 * TraditionDetail — dedicated page for a single tradition.
 *
 * Reads slug from URL (name with spaces → dashes, URL-encoded), looks up
 * the canonical entry from /lib/traditions.js, and renders the full
 * breakdown: summary, era/region, beliefs, practices, key figures, and
 * the correlations that bridge this tradition to others.
 *
 * Also surfaces:
 *  - Related Weekly correlations (filtered client-side by name mention)
 *  - "Ask the Oracle" CTA which prefills the Oracle input via ?q=...
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, GitCompare } from "lucide-react";
import { TRADITIONS, TRADITION_BY_SLUG } from "../lib/traditions";
import { parentOf, branchesOf } from "../lib/traditionRelations";
import { traditionPath } from "./Traditions";
import { api } from "../lib/api";

// Reverse of traditionPath() in Traditions.jsx.
// "Vedic-Astrology" (URL-decoded) → look up by name == "Vedic Astrology".
// Also accepts the raw slug ("vedic-astrology") as a fallback.
function resolveTradition(param) {
  if (!param) return null;
  const decoded = decodeURIComponent(param);
  const nameGuess = decoded.replace(/-/g, " ");
  // 1. Match by display name (case-insensitive).
  const byName = TRADITIONS.find(
    (t) => t.name.toLowerCase() === nameGuess.toLowerCase()
  );
  if (byName) return byName;
  // 2. Fallback: match by slug (canonical lowercase id).
  return TRADITION_BY_SLUG[decoded.toLowerCase()] || null;
}

export default function TraditionDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const tradition = useMemo(() => resolveTradition(name), [name]);
  const parent = useMemo(() => (tradition ? parentOf(tradition.slug) : null), [tradition]);
  const branches = useMemo(() => (tradition ? branchesOf(tradition.slug) : []), [tradition]);

  const [related, setRelated] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(true);

  useEffect(() => {
    if (!tradition) return;
    let cancelled = false;
    setLoadingRelated(true);
    api
      .get("/correlations/feed", { params: { sort: "hot", limit: 60 } })
      .then(({ data }) => {
        if (cancelled) return;
        const needle = tradition.name.toLowerCase();
        const matches = (data || []).filter((it) => {
          const blob = [
            it.title,
            it.content,
            ...(it.bullets || []),
          ]
            .filter(Boolean)
            .join(" \n ")
            .toLowerCase();
          return blob.includes(needle);
        });
        setRelated(matches.slice(0, 6));
      })
      .catch(() => {
        if (!cancelled) setRelated([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRelated(false);
      });
    return () => { cancelled = true; };
  }, [tradition]);

  if (!tradition) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-40 text-center" data-testid="tradition-not-found">
        <div className="label mb-6">Not in the lexicon</div>
        <h1 className="serif text-4xl text-foreground mb-6">That tradition isn&apos;t catalogued yet.</h1>
        <Link to="/traditions" className="btn-ghost" data-testid="tradition-not-found-back">
          ← Back to traditions
        </Link>
      </div>
    );
  }

  const askOracle = () => {
    const q = `Walk me through ${tradition.name}: its core insight, its practice, and where it converges with other traditions.`;
    navigate(`/oracle?q=${encodeURIComponent(q)}&ask=1`);
  };

  const askCompare = (other) => {
    const trimmed = (other || "").trim();
    if (!trimmed) return;
    pushRecentCompare(trimmed); // Remember for next visit.
    const q = `Draw the correlation between ${tradition.name} and ${trimmed}. Where do they name the same truth differently? Where do they actually diverge? Cite the specific terms each tradition uses.`;
    navigate(`/oracle?q=${encodeURIComponent(q)}&ask=1`);
  };

  return (
    <article data-testid="tradition-detail-page" className="max-w-4xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
      <Link
        to="/traditions"
        data-testid="tradition-back"
        className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-[hsl(var(--primary))] mb-10"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> All traditions
      </Link>

      {/* HEADER */}
      <header className="mb-12">
        <div className="label mb-5 text-[hsl(var(--accent))]">Tradition</div>
        {parent && (
          <Link
            to={traditionPath(parent)}
            data-testid="tradition-parent-link"
            className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-[hsl(var(--primary))] mb-3"
          >
            <span className="text-foreground/40">Part of</span>
            <span className="text-[hsl(var(--primary))]">{parent.name}</span>
            <span className="text-foreground/40">→</span>
          </Link>
        )}
        <h1
          data-testid="tradition-name"
          className="serif text-5xl sm:text-6xl text-foreground leading-[1.05]"
        >
          {tradition.name}
        </h1>
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          <span data-testid="tradition-era">
            <span className="text-foreground/50">Era · </span>{tradition.era}
          </span>
          <span data-testid="tradition-region">
            <span className="text-foreground/50">Region · </span>{tradition.region}
          </span>
        </div>
        <p className="mt-8 serif italic text-[19px] sm:text-[22px] leading-relaxed text-foreground/90 max-w-3xl">
          {tradition.summary}
        </p>
        {branches.length > 0 && (
          <div data-testid="tradition-branches" className="mt-8 pt-6 hairline-t">
            <div className="label mb-3 text-muted-foreground">Branches &amp; inward streams</div>
            <ul className="flex flex-wrap gap-2">
              {branches.map((b) => (
                <li key={b.slug}>
                  <Link
                    to={traditionPath(b)}
                    data-testid={`tradition-branch-${b.slug}`}
                    className="inline-flex items-center gap-2 px-4 py-2 hairline text-[14px] text-foreground/90 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
                  >
                    <span className="serif italic">{b.name}</span>
                    <span className="text-foreground/40 group-hover:text-[hsl(var(--primary))]">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      {/* BREAKDOWN SECTIONS */}
      <div className="space-y-12">
        <Section title="Core beliefs" testid="tradition-beliefs">
          <ol className="space-y-3">
            {tradition.beliefs.map((b, i) => (
              <li key={`${i}-${b.slice(0, 40)}`} className="flex gap-4 text-[15px] text-foreground/85 leading-relaxed">
                <span className="serif text-xl text-[hsl(var(--primary))] tabular-nums leading-none mt-0.5 w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Section title="Practices" testid="tradition-practices">
          <ul className="flex flex-wrap gap-2">
            {tradition.practices.map((p) => (
              <li
                key={p}
                className="px-3 py-1.5 hairline text-[13px] text-foreground/85 bg-secondary/30"
              >
                {p}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Key figures" testid="tradition-figures">
          <ul className="space-y-1.5">
            {tradition.figures.map((f) => (
              <li key={f} className="text-[15px] text-foreground/85 leading-relaxed">
                · {f}
              </li>
            ))}
          </ul>
        </Section>

        {tradition.correlations?.length > 0 && (
          <Section title="Bridges to other traditions" testid="tradition-correlations">
            <ul className="space-y-2">
              {tradition.correlations.map((c) => (
                <li
                  key={c}
                  className="serif italic text-[16px] text-foreground/90 leading-relaxed"
                >
                  {c}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* ASK THE ORACLE CTA */}
        <OracleCTA tradition={tradition} onGoDeeper={askOracle} onCompare={askCompare} />

        {/* RELATED WEEKLY CORRELATIONS */}
        <section data-testid="tradition-related" className="pt-4">
          <div className="hairline-b pb-3 mb-6 flex items-baseline justify-between gap-4">
            <h2 className="serif text-2xl sm:text-3xl text-foreground">
              From the Weekly feed
            </h2>
            <Link
              to="/weekly"
              className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-[hsl(var(--primary))]"
              data-testid="tradition-related-all"
            >
              All →
            </Link>
          </div>

          {loadingRelated ? (
            <p className="text-muted-foreground italic text-[14px]">
              Looking for echoes…
            </p>
          ) : related.length === 0 ? (
            <p className="text-muted-foreground italic text-[14px]" data-testid="tradition-related-empty">
              No one&apos;s woven {tradition.name} into a correlation here yet. Maybe you&apos;re the first.
            </p>
          ) : (
            <ul className="space-y-3">
              {related.map((it) => (
                <li key={it.id}>
                  <Link
                    to={`/weekly#item-${it.id}`}
                    data-testid={`tradition-related-item-${it.id}`}
                    className="block p-4 hairline hover:border-[hsl(var(--primary))] transition-colors"
                  >
                    <div className="serif text-[17px] text-foreground leading-snug mb-1">
                      {it.title}
                    </div>
                    <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/75">
                      by {it.user_handle ? `@${it.user_handle}` : (it.user_name || "anonymous")}
                      {typeof it.bumps_count === "number" && ` · ${it.bumps_count} bump${it.bumps_count === 1 ? "" : "s"}`}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </article>
  );
}

function Section({ title, testid, children }) {
  return (
    <section data-testid={testid}>
      <h2 className="mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mb-5">
        {title}
      </h2>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------- ORACLE CTA
//
// Two modes:
//   1. "Go Deeper"  — walk through THIS tradition in depth.
//   2. "Compare"    — pick (or type) ANOTHER tradition; the Oracle is asked
//                     to draw the correlation between the two.
// Free-text fallback lets the seeker bring a tradition we haven't catalogued.
//
// Recent compares are remembered in localStorage so a returning seeker sees
// their last few picks bubble to the top of the suggestion list.

const RECENT_KEY = "cor_recent_compares";
const RECENT_MAX = 5;

function readRecentCompares() {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch (err) {
    // localStorage may throw in private-browse / sandboxed contexts.
    console.warn("[TraditionDetail] readRecentCompares failed:", err);
    return [];
  }
}

function pushRecentCompare(name) {
  if (typeof window === "undefined") return;
  const clean = (name || "").trim();
  if (!clean) return;
  try {
    const cur = readRecentCompares();
    // De-dupe case-insensitively, keep most-recent first.
    const filtered = cur.filter((s) => s.toLowerCase() !== clean.toLowerCase());
    const next = [clean, ...filtered].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (err) {
    // Quota exceeded / private mode — non-fatal, just lose the memory.
    console.warn("[TraditionDetail] pushRecentCompare failed:", err);
  }
}

function removeRecentCompare(name) {
  if (typeof window === "undefined") return;
  try {
    const cur = readRecentCompares();
    const next = cur.filter((s) => s.toLowerCase() !== (name || "").toLowerCase());
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (err) {
    console.warn("[TraditionDetail] removeRecentCompare failed:", err);
  }
}

function OracleCTA({ tradition, onGoDeeper, onCompare }) {
  const [mode, setMode] = React.useState("deeper");
  const [other, setOther] = React.useState("");
  const [suggestOpen, setSuggestOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const [recent, setRecent] = React.useState([]);
  const inputRef = React.useRef(null);
  const boxRef = React.useRef(null);

  // Load recents once on mount.
  React.useEffect(() => {
    setRecent(readRecentCompares());
  }, []);

  const otherTraditions = React.useMemo(
    () => TRADITIONS.filter((t) => t.slug !== tradition.slug).map((t) => t.name),
    [tradition.slug],
  );

  // Ranked autocomplete: prefix matches first, then substring matches.
  // Empty query → recents first, then catalogued options as a "browse" list.
  const suggestions = React.useMemo(() => {
    const q = other.trim().toLowerCase();
    if (!q) {
      // Browse mode: recents on top, then catalogue (deduped, capped at 8).
      const seen = new Set();
      const merged = [];
      for (const n of recent) {
        const k = n.toLowerCase();
        if (!seen.has(k)) { seen.add(k); merged.push(n); }
      }
      for (const n of otherTraditions) {
        const k = n.toLowerCase();
        if (!seen.has(k)) { seen.add(k); merged.push(n); }
      }
      return merged.slice(0, 8);
    }
    const starts = [];
    const contains = [];
    // Include recents in the typed-search results too (they might match).
    const pool = [...recent, ...otherTraditions.filter((n) => !recent.some((r) => r.toLowerCase() === n.toLowerCase()))];
    for (const n of pool) {
      const ln = n.toLowerCase();
      if (ln === q) continue; // exact match — no point suggesting what's already typed
      if (ln.startsWith(q)) starts.push(n);
      else if (ln.includes(q)) contains.push(n);
    }
    return [...starts, ...contains].slice(0, 8);
  }, [other, otherTraditions, recent]);

  // Ghost-text inline completion: the first prefix-matching suggestion, minus
  // what the user has already typed. Disappears the moment they edit/erase.
  const ghost = React.useMemo(() => {
    const q = other;
    if (!q) return "";
    const head = suggestions[0];
    if (!head) return "";
    if (head.toLowerCase().startsWith(q.toLowerCase()) && head.length > q.length) {
      return head.slice(q.length);
    }
    return "";
  }, [other, suggestions]);

  // Clamp highlight if suggestion list shrinks.
  React.useEffect(() => {
    if (highlight >= suggestions.length) setHighlight(0);
  }, [suggestions.length, highlight]);

  // Close on outside click.
  React.useEffect(() => {
    if (!suggestOpen) return;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [suggestOpen]);

  const choose = (name) => {
    setOther(name);
    setSuggestOpen(false);
    inputRef.current?.focus();
  };

  const onKey = (e) => {
    // Tab / Right-arrow at end → accept ghost-text completion.
    // Use the canonical-cased suggestion (not raw typed text + ghost) so
    // "tao" + Tab commits "Taoism" rather than "taoism".
    if (ghost && (e.key === "Tab" || (e.key === "ArrowRight" && e.target.selectionStart === other.length))) {
      e.preventDefault();
      if (suggestions[0]) setOther(suggestions[0]);
      else setOther(other + ghost);
      return;
    }
    if (!suggestOpen) {
      if (e.key === "ArrowDown") {
        setSuggestOpen(true);
        setHighlight(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % Math.max(1, suggestions.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % Math.max(1, suggestions.length));
    } else if (e.key === "Enter") {
      if (suggestions[highlight]) {
        e.preventDefault();
        choose(suggestions[highlight]);
      }
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  };

  const onCompareSubmit = (e) => {
    e.preventDefault();
    onCompare(other);
  };

  return (
    <section data-testid="tradition-ask-oracle" className="p-7 hairline bg-secondary/30">
      {/* MODE SWITCH */}
      <div
        role="tablist"
        aria-label="Oracle mode"
        className="flex items-center gap-1 mb-6 border border-border rounded-full p-1 w-fit bg-background/60"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === "deeper"}
          onClick={() => setMode("deeper")}
          data-testid="oracle-mode-deeper"
          className={`mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full transition-colors inline-flex items-center gap-2 ${
            mode === "deeper"
              ? "bg-[hsl(var(--primary))] text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Sparkles size={13} strokeWidth={1.6} /> Go Deeper
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "compare"}
          onClick={() => setMode("compare")}
          data-testid="oracle-mode-compare"
          className={`mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full transition-colors inline-flex items-center gap-2 ${
            mode === "compare"
              ? "bg-[hsl(var(--primary))] text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <GitCompare size={13} strokeWidth={1.6} /> Compare
        </button>
      </div>

      {mode === "deeper" ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <h3 className="serif text-2xl text-foreground mb-2">Go deeper into {tradition.name}</h3>
            <p className="text-muted-foreground text-[15px] max-w-xl leading-relaxed">
              Have the Oracle walk through {tradition.name} in depth — its core insight,
              its practices, and the places it converges with other paths.
            </p>
          </div>
          <button
            type="button"
            onClick={onGoDeeper}
            data-testid="tradition-ask-oracle-btn"
            className="btn-gold inline-flex items-center gap-2 whitespace-nowrap"
          >
            <Sparkles size={16} strokeWidth={1.5} /> Ask the Oracle
          </button>
        </div>
      ) : (
        <form onSubmit={onCompareSubmit} className="space-y-5" data-testid="tradition-compare-form">
          <div>
            <h3 className="serif text-2xl text-foreground mb-2">Compare {tradition.name} with…</h3>
            <p className="text-muted-foreground text-[15px] max-w-xl leading-relaxed">
              Pick another tradition (or type one we haven&apos;t catalogued). The Oracle
              will draw the correlation between the two — where they name the same
              truth differently, and where they genuinely diverge.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div ref={boxRef} className="relative flex-1">
              {/* Ghost-text completion sits behind the real input. The visible portion
                  begins where the user's typed text ends — pure CSS layering, no
                  contenteditable hackery. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex items-center text-[14px] mono whitespace-pre overflow-hidden"
                style={{ paddingLeft: "0px" }}
              >
                <span className="invisible">{other}</span>
                <span className="text-muted-foreground/45" data-testid="tradition-compare-ghost">{ghost}</span>
              </div>

              <input
                ref={inputRef}
                type="text"
                value={other}
                onChange={(e) => { setOther(e.target.value); setSuggestOpen(true); setHighlight(0); }}
                onFocus={() => setSuggestOpen(true)}
                onKeyDown={onKey}
                placeholder="Start typing a tradition…"
                data-testid="tradition-compare-input"
                className="input-line w-full text-[14px] mono bg-transparent relative"
                autoComplete="off"
                role="combobox"
                aria-expanded={suggestOpen}
                aria-autocomplete="list"
                aria-controls="tradition-suggestions"
              />

              {suggestOpen && suggestions.length > 0 && (
                <ul
                  id="tradition-suggestions"
                  role="listbox"
                  data-testid="tradition-compare-suggestions"
                  className="absolute z-30 left-0 right-0 top-full mt-1 max-h-72 overflow-auto bg-background border border-border shadow-lg rounded-md py-1"
                >
                  {suggestions.map((n, i) => {
                    const isRecent = recent.some((r) => r.toLowerCase() === n.toLowerCase());
                    return (
                      <li
                        key={n}
                        role="option"
                        aria-selected={i === highlight}
                        data-testid={`tradition-compare-suggestion-${n.replace(/\s+/g, "-").toLowerCase()}`}
                        onMouseDown={(e) => { e.preventDefault(); choose(n); }}
                        onMouseEnter={() => setHighlight(i)}
                        className={`px-3 py-2 text-[14px] cursor-pointer flex items-center justify-between gap-3 ${
                          i === highlight
                            ? "bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]"
                            : "text-foreground/85 hover:bg-secondary/60"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          {highlightMatch(n, other)}
                          {isRecent && (
                            <span className="mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/65 border border-border px-1.5 py-0.5 rounded">
                              Recent
                            </span>
                          )}
                        </span>
                        {i === highlight && (
                          <span className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70">↵</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <button
              type="submit"
              disabled={!other.trim()}
              data-testid="tradition-compare-submit"
              className="btn-gold inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <GitCompare size={16} strokeWidth={1.5} /> Draw the correlation
            </button>
          </div>

          {/* Hint line — surfaces the keyboard affordance for power-users */}
          {ghost && (
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/65 -mt-2">
              Press Tab to complete · ↓ to browse
            </p>
          )}

          {/* Quick chips — recents if the seeker has any, else our top-5 picks */}
          {(() => {
            const showRecents = recent.length > 0;
            const chips = showRecents ? recent : otherTraditions.slice(0, 5);
            return (
              <div className="flex flex-wrap gap-2 pt-1" data-testid={showRecents ? "tradition-compare-recent-row" : "tradition-compare-quick-row"}>
                <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 self-center mr-1">
                  {showRecents ? "Recent:" : "Quick:"}
                </span>
                {chips.map((n) => (
                  <span
                    key={n}
                    className="inline-flex items-center hairline overflow-hidden text-[12px] text-foreground/80 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors group"
                  >
                    <button
                      type="button"
                      onClick={() => onCompare(n)}
                      data-testid={`tradition-compare-chip-${n.replace(/\s+/g, "-").toLowerCase()}`}
                      className="px-3 py-1"
                    >
                      {n}
                    </button>
                    {showRecents && (
                      <button
                        type="button"
                        aria-label={`Remove ${n} from recents`}
                        onClick={() => {
                          removeRecentCompare(n);
                          setRecent(readRecentCompares());
                        }}
                        data-testid={`tradition-compare-recent-remove-${n.replace(/\s+/g, "-").toLowerCase()}`}
                        className="px-2 py-1 border-l border-border/60 text-muted-foreground/60 hover:text-[hsl(var(--primary))] hover:bg-secondary/60 transition-colors"
                        tabIndex={-1}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            );
          })()}
        </form>
      )}
    </section>
  );
}


// Bold the matched substring inside a suggestion label.
function highlightMatch(label, query) {
  const q = (query || "").trim();
  if (!q) return label;
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return label;
  return (
    <>
      {label.slice(0, idx)}
      <span className="font-semibold text-[hsl(var(--primary))]">
        {label.slice(idx, idx + q.length)}
      </span>
      {label.slice(idx + q.length)}
    </>
  );
}

